import { useAppContext } from "contexts/AppContext";
import { useEffect, useState } from 'react';
import { Badge, Button, Col, Form, InputGroup, Modal, Row } from "react-bootstrap";
import { CaretDownFill, CaretUpFill, ChevronExpand, FunnelFill } from "react-bootstrap-icons";
import Select from "react-select";

const FiltersModal = ({ show, setShow, filters, callback, group }) => {
  const { appState } = useAppContext();
  const [myFilters, setMyFilters] = useState({ ...filters });
  const groupOptions = appState.resources.itemGroups.map(group => ({ label: group.name, value: group }));
  const tagOptions = appState.resources.itemTags.map(tag => ({ label: tag, value: tag }));

  const sort = (key, myFilters) => {
    // finding direction
    let direction = myFilters.sortBy[key].direction;
    if (!direction) direction = 'asc';
    else if (direction === 'asc') direction = 'desc';
    else if (direction === 'desc') direction = null;

    // finding position
    let position = myFilters.sortBy[key].position;
    if (direction === 'asc') {
      position = Math.max(
        myFilters.sortBy.displayName.position,
        myFilters.sortBy.itemNumber.position,
        myFilters.sortBy.groupName.position,
        myFilters.sortBy.itemPrice.position,
        myFilters.sortBy.itemCreatedAt.position,
      ) + 1;
    } else if (direction === null) {
      myFilters.sortBy.displayName.position -= myFilters.sortBy.displayName.position > position ? 1 : 0;
      myFilters.sortBy.itemNumber.position -= myFilters.sortBy.itemNumber.position > position ? 1 : 0;
      myFilters.sortBy.groupName.position -= myFilters.sortBy.groupName.position > position ? 1 : 0;
      myFilters.sortBy.itemPrice.position -= myFilters.sortBy.itemPrice.position > position ? 1 : 0;
      myFilters.sortBy.itemCreatedAt.position -= myFilters.sortBy.itemCreatedAt.position > position ? 1 : 0;
      position = 0;
    }

    setMyFilters({
      ...myFilters,
      sortBy: {
        ...myFilters.sortBy,
        [key]: {
          direction,
          position
        }
      }
    });
  };

  const applyChanges = () => {
    setShow(false);
    callback({
      ...myFilters,
      activeFiltersCount: getFiltersCount(myFilters)
    });
  };

  const getFiltersCount = (filters) => {
    let count = 0;
    if (filters.itemGroups.length) count += 1;
    if (filters.excludedItemGroups.length) count += 1;
    if (filters.tags.length) count += 1;
    if (filters.excludedTags.length) count += 1;
    if (filters.onlyAvailables) count += 1;
    if (filters.withPhoto) count += 1;
    if (filters.priceFrom || filters.priceTo) count += 1;
    if (
      filters.sortBy.displayName.position > 1 ||
      filters.sortBy.itemNumber.position > 1 ||
      filters.sortBy.groupName.position > 1 ||
      filters.sortBy.itemPrice.position > 1 ||
      filters.sortBy.itemCreatedAt.position > 1) count += 1;

    return count;
  };

  const dismiss = () => {
    setMyFilters(filters);
    setShow(false);
  };

  const clearFilters = () => {
    setMyFilters(filters => ({
      itemGroups: [],
      excludedItemGroups: [],
      tags: [],
      excludedTags: [],
      onlyAvailables: false,
      withPhoto: false,
      priceFrom: null,
      priceTo: null,
      sortBy: {
        displayName: {
          direction: null,
          position: 0
        },
        itemNumber: {
          direction: null,
          position: 0
        },
        groupName: {
          direction: null,
          position: 0
        },
        itemPrice: {
          direction: null,
          position: 0
        },
        itemCreatedAt: {
          direction: null,
          position: 0
        }
      },
      levels: filters.levels,
      activeFiltersCount: 0
    }));
  };

  useEffect(() => {
    setMyFilters({
      ...filters,
      itemGroups: groupOptions.filter(group => filters.itemGroups.map(g => g.value?.id).includes(group.value.id)),
      excludedItemGroups: groupOptions.filter(group => filters.excludedItemGroups.map(g => g.value?.id).includes(group.value.id)),
      tags: tagOptions.filter(tag => filters.tags.map(x => x.value).includes(tag.value)),
      excludedTags: tagOptions.filter(tag => filters.excludedTags.map(x => x.value).includes(tag.value))
    });
  }, [filters]);


  return (
    <Modal size="lg" backdrop="static" show={show} onHide={dismiss}>
      <Modal.Header closeButton>
        <FunnelFill size={25} />
        <Modal.Title>Filters and Sort</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="mt-2 border-bottom pb-2">
          <Col xs={12} md={4}>
            <Form.Label>Sort By</Form.Label>
          </Col>
          <Col>
            <Row>
              <Col xs={12} md={6} className="cursor-pointer" onClick={() => sort('displayName', myFilters)}>
                <Button variant="outline-dark" className="border-0">
                  {myFilters.sortBy.displayName.direction === 'asc' && <CaretUpFill size={16} />}
                  {myFilters.sortBy.displayName.direction === 'desc' && <CaretDownFill size={16} />}
                  {!myFilters.sortBy.displayName.direction && <ChevronExpand size={16} />}
                  <span className="form-text">{myFilters.sortBy.displayName.position || ""}</span>
                  Display Name
                </Button>
              </Col>
              <Col
                xs={12} md={6}
                className="cursor-pointer"
                onClick={() => sort('itemNumber', myFilters)}
              >
                <Button variant="outline-dark" className="border-0">
                  {myFilters.sortBy.itemNumber.direction === 'asc' && <CaretUpFill size={16} />}
                  {myFilters.sortBy.itemNumber.direction === 'desc' && <CaretDownFill size={16} />}
                  {!myFilters.sortBy.itemNumber.direction && <ChevronExpand size={16} />}
                  <span className="form-text">{myFilters.sortBy.itemNumber.position || ""}</span>
                  Item Code
                </Button>
              </Col>
              <Col
                xs={12} md={6}
                className="cursor-pointer"
                onClick={() => sort('groupName', myFilters)}
              >
                <Button variant="outline-dark" className="border-0">
                  {myFilters.sortBy.groupName.direction === 'asc' && <CaretUpFill size={16} />}
                  {myFilters.sortBy.groupName.direction === 'desc' && <CaretDownFill size={16} />}
                  {!myFilters.sortBy.groupName.direction && <ChevronExpand size={16} />}
                  <span className="form-text">{myFilters.sortBy.groupName.position || ""}</span>
                  Group Name
                </Button>
              </Col>
              <Col xs={12} md={6} className="cursor-pointer" onClick={() => sort('itemPrice', myFilters)}>
                <Button variant="outline-dark" className="border-0">
                  {myFilters.sortBy.itemPrice.direction === 'asc' && <CaretUpFill size={16} />}
                  {myFilters.sortBy.itemPrice.direction === 'desc' && <CaretDownFill size={16} />}
                  {!myFilters.sortBy.itemPrice.direction && <ChevronExpand size={16} />}
                  <span className="form-text">{myFilters.sortBy.itemPrice.position || ""}</span>
                  Item Price
                </Button>
              </Col>
              <Col xs={12} md={6} className="cursor-pointer" onClick={() => sort('itemCreatedAt', myFilters)}>
                <Button variant="outline-dark" className="border-0">
                  {myFilters.sortBy.itemCreatedAt.direction === 'asc' && <CaretUpFill size={16} />}
                  {myFilters.sortBy.itemCreatedAt.direction === 'desc' && <CaretDownFill size={16} />}
                  {!myFilters.sortBy.itemCreatedAt.direction && <ChevronExpand size={16} />}
                  <span className="form-text">{myFilters.sortBy.itemCreatedAt.position || ""}</span>
                  Item Creation Date
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
        <Row className="mt-3">
          <Col xs={12} md={4}>
            <Form.Label>Item Groups</Form.Label>
          </Col>
          <Col>
            <Select
              value={myFilters.itemGroups}
              options={groupOptions}
              isOptionSelected={(option, options) => options.find(x => x.value.id === option.value.id)}
              isMulti
              onChange={(e) => setMyFilters({ ...myFilters, itemGroups: e })}
              placeholder="Included Groups"
              isDisabled={!!group}
            />
          </Col>
          <Col>
            <Select
              value={myFilters.excludedItemGroups}
              options={groupOptions}
              isOptionSelected={(option, options) => options.find(x => x.value.id === option.value.id)}
              isMulti
              onChange={(e) => setMyFilters({ ...myFilters, excludedItemGroups: e })}
              placeholder="Excluded Groups"
              isDisabled={!!group}
            />
          </Col>
        </Row>
        <Row className="mt-3">
          <Col xs={12} md={4}>
            <Form.Label>Tags</Form.Label>
          </Col>
          <Col>
            <Select
              value={myFilters.tags}
              options={tagOptions}
              onChange={(e) => setMyFilters({ ...myFilters, tags: e })}
              isMulti
              placeholder="Included Tags"
            />
          </Col>
          <Col>
            <Select
              value={myFilters.excludedTags}
              options={tagOptions}
              isMulti
              onChange={(e) => setMyFilters({ ...myFilters, excludedTags: e })}
              placeholder="Excluded Tags"
            />
          </Col>
        </Row>
        {appState.auth?.user?.entity?.config?.inventory && <Row className="mt-3">
          <Col xs={12} md={4}>
            <Form.Label>Only Availables</Form.Label>
          </Col>
          <Col>
            <Form.Check
              type="switch"
              checked={myFilters.onlyAvailables}
              onChange={(e) => setMyFilters({ ...myFilters, onlyAvailables: e.target.checked })}
              label="Only shows the available items for sale (may take a while)"
            />
          </Col>
        </Row>}
        <Row className="mt-3">
          <Col xs={12} md={4}>
            <Form.Label>With Photo</Form.Label>
          </Col>
          <Col>
            <Form.Check
              type="switch"
              onChange={(e) => setMyFilters({ ...myFilters, withPhoto: e.target.checked })}
              checked={myFilters.withPhoto}
              label="Only shows the items with photos"
            />
          </Col>
        </Row>
        <Row className="mt-3">
          <Col xs={12} md={4}>
            <Form.Label>Item Price Range</Form.Label>
          </Col>
          <Col>
            <InputGroup className="mb-3">
              <InputGroup.Text>$</InputGroup.Text>
              <Form.Control
                type="number"
                placeholder="From"
                value={myFilters.priceFrom}
                onChange={(e) => setMyFilters({ ...myFilters, priceFrom: parseFloat(e.target.value) })}
              />
            </InputGroup>
          </Col>
          <Col>
            <InputGroup className="mb-3">
              <InputGroup.Text>$</InputGroup.Text>
              <Form.Control
                type="number"
                placeholder="To"
                value={myFilters.priceTo}
                onChange={(e) => setMyFilters({ ...myFilters, priceTo: parseFloat(e.target.value) })}
              />
            </InputGroup></Col>
        </Row>
        <p className="text-muted mt-3">Note: To reset filters, click on <Badge bg="secondary" className="fw-normal">Reset All</Badge> and then <Badge bg="primary" className="fw-normal">Apply changes</Badge></p>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <Button variant="danger" onClick={dismiss}>Cancel</Button>
        <span>
          <Button variant="secondary" className="mx-2" onClick={clearFilters}>Reset All</Button>
          <Button onClick={applyChanges}>Apply Changes</Button>
        </span>
      </Modal.Footer>
    </Modal>
  );
};

export default FiltersModal;